import { useState } from 'react';
import type { ReactNode } from 'react';
import { Drawer, Typography } from 'antd';
import {
  CameraOutlined,
  SwapOutlined,
  DeleteOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

interface PhotoActionSheetProps {
  open: boolean;
  hasPhoto: boolean;
  busy: boolean;
  onClose: () => void;
  onUpload: () => void;
  onDelete: () => void;
}

export function PhotoActionSheet({
  open,
  hasPhoto,
  busy,
  onClose,
  onUpload,
  onDelete,
}: PhotoActionSheetProps) {
  const { t } = useTranslation(['common', 'provider']);

  return (
    <Drawer
      open={open}
      placement="bottom"
      onClose={onClose}
      closable={false}
      maskClosable={!busy}
      height="auto"
      styles={{
        body: {
          padding: '12px 16px',
          paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
          background: 'var(--bl-surface, #fff)',
        },
        wrapper: { borderRadius: '20px 20px 0 0', overflow: 'hidden' },
        mask: { background: 'rgba(15,23,42,0.45)' },
      }}
    >
      {/* Drag handle */}
      <div
        aria-hidden
        style={{
          width: 36,
          height: 4,
          borderRadius: 2,
          background: 'var(--bl-divider, #e5e7eb)',
          margin: '0 auto 18px',
        }}
      />

      {/* Hero icon + title */}
      <div style={{ textAlign: 'center', marginBottom: 18 }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background:
              'linear-gradient(135deg, rgba(51,144,236,0.14) 0%, rgba(51,144,236,0.28) 100%)',
            border: '1px solid rgba(51,144,236,0.20)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 24,
            color: 'var(--bl-accent, #3390ec)',
            margin: '0 auto 12px',
            boxShadow: '0 6px 18px rgba(51,144,236,0.14)',
          }}
        >
          <CameraOutlined />
        </div>
        <Typography.Text
          style={{
            fontSize: 17,
            fontWeight: 700,
            display: 'block',
            color: 'var(--bl-text-strong, #0f172a)',
            letterSpacing: -0.2,
          }}
        >
          {t('provider:profile.photo.sheetTitle')}
        </Typography.Text>
      </div>

      {/* Action rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
        {hasPhoto ? (
          <>
            <ActionRow
              icon={<SwapOutlined />}
              color="#3390ec"
              label={t('provider:profile.photo.change')}
              disabled={busy}
              onClick={onUpload}
            />
            <ActionRow
              icon={<DeleteOutlined />}
              color="#ef4444"
              label={t('provider:profile.photo.delete')}
              disabled={busy}
              onClick={onDelete}
            />
          </>
        ) : (
          <ActionRow
            icon={<UploadOutlined />}
            color="#3390ec"
            label={t('provider:profile.photo.upload')}
            disabled={busy}
            onClick={onUpload}
            variant="primary"
          />
        )}
      </div>

      {/* Cancel */}
      <CancelButton onClick={onClose} disabled={busy} label={t('common:actions.cancel')} />
    </Drawer>
  );
}

function ActionRow({
  icon,
  color,
  label,
  disabled,
  onClick,
  variant = 'default',
}: {
  icon: ReactNode;
  color: string;
  label: string;
  disabled: boolean;
  onClick: () => void;
  variant?: 'default' | 'primary';
}) {
  const [pressed, setPressed] = useState(false);
  const isPrimary = variant === 'primary';

  return (
    <button
      type="button"
      onClick={() => !disabled && onClick()}
      disabled={disabled}
      onTouchStart={() => !disabled && setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      onTouchCancel={() => setPressed(false)}
      onMouseDown={() => !disabled && setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        padding: '12px 14px',
        borderRadius: 14,
        border: isPrimary ? `1px solid ${color}26` : `1px solid var(--bl-divider, #ebebeb)`,
        background: isPrimary
          ? `linear-gradient(135deg, ${color}10 0%, ${color}22 100%)`
          : pressed
            ? `${color}08`
            : 'var(--bl-surface, #fff)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transform: pressed && !disabled ? 'scale(0.985)' : 'scale(1)',
        transition: 'background 0.15s ease, border 0.15s ease, transform 0.1s ease',
        WebkitTapHighlightColor: 'transparent',
        outline: 'none',
        textAlign: 'left',
        fontFamily: 'inherit',
      }}
    >
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: 11,
          background: `linear-gradient(135deg, ${color}14 0%, ${color}26 100%)`,
          border: `1px solid ${color}1a`,
          color,
          fontSize: 16,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <Typography.Text
        style={{
          flex: 1,
          fontSize: 14,
          fontWeight: 700,
          color: 'var(--bl-text-strong, #0f172a)',
          lineHeight: 1.35,
        }}
      >
        {label}
      </Typography.Text>
    </button>
  );
}

function CancelButton({
  label,
  disabled,
  onClick,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
}) {
  const [pressed, setPressed] = useState(false);
  return (
    <button
      type="button"
      onClick={() => !disabled && onClick()}
      disabled={disabled}
      onTouchStart={() => !disabled && setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      onTouchCancel={() => setPressed(false)}
      onMouseDown={() => !disabled && setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      style={{
        width: '100%',
        minHeight: 46,
        padding: '0 16px',
        border: 'none',
        borderRadius: 12,
        background: pressed
          ? 'var(--bl-surface-soft, #ebedf0)'
          : 'var(--bl-surface-soft, #f7f8fa)',
        color: 'var(--bl-text-muted, #6b7280)',
        fontSize: 14,
        fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transform: pressed && !disabled ? 'scale(0.985)' : 'scale(1)',
        transition: 'background 0.15s ease, transform 0.1s ease',
        WebkitTapHighlightColor: 'transparent',
        opacity: disabled ? 0.55 : 1,
        outline: 'none',
        fontFamily: 'inherit',
      }}
    >
      {label}
    </button>
  );
}
